import { useTranslation } from "react-i18next";
import ScrollReveal from "./ScrollReveal";
import processImg from "@/assets/process-integration.webp";

const OfferSection = ({ onDemoClick }: { onDemoClick: () => void }) => {
  const { t } = useTranslation();

  const capexItems = t("offer.capexItems", { returnObjects: true }) as string[];
  const opexItems = t("offer.opexItems", { returnObjects: true }) as string[];

  const steps = [
    { icon: "🔍", title: t("offer.step1"), desc: t("offer.step1desc") },
    { icon: "📦", title: t("offer.step2"), desc: t("offer.step2desc") },
    { icon: "🔧", title: t("offer.step3"), desc: t("offer.step3desc") },
    { icon: "✅", title: t("offer.step4"), desc: t("offer.step4desc") },
    { icon: "🎓", title: t("offer.step5"), desc: t("offer.step5desc") },
  ];

  return (
    <section id="offer" className="py-8 lg:py-14 relative">
      <div className="container mx-auto px-4 space-y-6 lg:space-y-10">
        <ScrollReveal>
          <div className="space-y-4 max-w-2xl">
            <p className="section-label">{t("offer.label")}</p>
            <h2 className="font-display text-3xl lg:text-5xl text-foreground">
              {t("offer.title")}
              <br />
              <span className="text-muted-foreground text-2xl lg:text-3xl">{t("offer.subtitle")}</span>
            </h2>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 gap-6">
          <ScrollReveal>
            <div className="card-surface p-8 h-full space-y-4">
              <span className="inline-block text-xs font-mono text-gold bg-gold/10 px-3 py-1 rounded-full">{t("offer.capexTag")}</span>
              <h3 className="font-display text-2xl text-foreground">{t("offer.capexTitle")}</h3>
              <p className="font-mono text-sm text-primary">{t("offer.capexPrice")}</p>
              <p className="text-xs text-muted-foreground font-body">{t("offer.capexRoi")}</p>
              <ul className="space-y-2 text-sm font-body text-muted-foreground">
                {capexItems.map((i) => (
                  <li key={i} className="flex gap-2"><span className="text-accent">✓</span>{i}</li>
                ))}
              </ul>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <div className="card-surface p-8 h-full space-y-4 border-primary/40 glow-cyan">
              <div className="flex gap-2">
                <span className="inline-block text-xs font-mono text-primary bg-primary/10 px-3 py-1 rounded-full">{t("offer.recommended")}</span>
                <span className="inline-block text-xs font-mono text-gold bg-gold/10 px-3 py-1 rounded-full">{t("offer.opexTag")}</span>
              </div>
              <h3 className="font-display text-2xl text-foreground">{t("offer.opexTitle")}</h3>
              <p className="font-mono text-sm text-primary">{t("offer.opexPrice")}</p>
              <p className="text-xs text-muted-foreground font-body">{t("offer.opexInvest")}</p>
              <ul className="space-y-2 text-sm font-body text-foreground">
                {opexItems.map((i) => (
                  <li key={i} className="flex gap-2"><span className="text-accent">✓</span>{i}</li>
                ))}
              </ul>
              <button onClick={onDemoClick} className="w-full bg-primary text-primary-foreground py-3 rounded-full font-body font-medium hover:glow-cyan-strong transition-all mt-2">
                {t("offer.demoCta")}
              </button>
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal>
          <div className="space-y-8">
            <h3 className="font-display text-2xl text-foreground text-center">{t("offer.processTitle")}</h3>
            <div className="group relative rounded-xl overflow-hidden border border-primary/20 transition-all duration-500 hover:scale-[1.01] hover:shadow-lg hover:shadow-primary/10 hover:border-primary/40">
              <img src={processImg} alt="" className="w-full h-auto opacity-60 transition-opacity duration-500 group-hover:opacity-80" loading="lazy" />
              <div className="absolute inset-0 bg-background/30 transition-colors duration-500 group-hover:bg-background/10" />
            </div>
            <div className="flex flex-col md:hidden gap-4">
              {steps.map((s, i) => (
                <div key={i} className="card-surface p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-lg shrink-0">{s.icon}</div>
                  <div>
                    <p className="font-body font-medium text-foreground text-sm">{s.title}</p>
                    <p className="text-xs text-muted-foreground font-body">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default OfferSection;
